import { Box } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- billing table uses bare DataGridPro for client-side totals + pinned-row footer; toolbar uses shared DataGridToolbar
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { addMonths, endOfDay, format, isValid, startOfDay } from 'date-fns';
import { fromJS } from 'immutable';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import { fetchProjectPerformedServices } from '@sophocles/features/projectServices/api';
import { getTotalReducer } from '@sophocles/features/projectServices/utils';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { formatNumberWithCommas } from '@sophocles/utils';

import InstallServicesBillingExcelButton from './InstallServicesBillingExcelButton';

function JobDetailTable({
  startDate: externalStart,
  endDate: externalEnd,
  onChangeDateRange,
  extraParams,
  extraToolbarContent,
}) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  // Tenant-level terms: the report spans customers, so no single CustomerType
  // override applies — the tenant vocabulary does.
  const siteTerm = useSiteTerm();
  const siteGroupTerm = useSiteGroupTerm();

  const [internalStart, setInternalStart] = useState(() => startOfDay(addMonths(new Date(), -1)));
  const [internalEnd, setInternalEnd] = useState(() => endOfDay(new Date()));

  const startDate = externalStart || internalStart;
  const endDate = externalEnd || internalEnd;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = {
      service_date__gte: format(startDate, 'yyyy-MM-dd'),
      service_date__lte: format(endDate, 'yyyy-MM-dd'),
      limit: 5000,
      offset: 0,
      ...extraParams,
    };
    fetchProjectPerformedServices(params)
      .then(data => {
        const results = data?.results || (Array.isArray(data) ? data : []);
        setRows(results);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate, extraParams]);

  const handleDateRangeChange = useCallback(
    (start, end) => {
      setInternalStart(start);
      setInternalEnd(end);
      if (onChangeDateRange) onChangeDateRange(start, end);
    },
    [onChangeDateRange],
  );

  useEffect(() => {
    if (onChangeDateRange) onChangeDateRange(startDate, endDate);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'service_date',
        headerName: t('projectServices.reporting.columns.date'),
        width: 120,
        disableColumnMenu: true,
        valueFormatter: value => (isValid(value) ? format(value, 'yyyy-MM-dd') : value),
      },
      {
        field: 'site_group_name',
        headerName: siteGroupTerm.singular,
        width: 160,
        disableColumnMenu: true,
        valueGetter: (value, row) => {
          const site = _.get(row, 'site', {});
          const siteGroups = _.get(site, 'site_groups', []);
          return _.get(siteGroups, '[0].name', '');
        },
      },
      {
        field: 'site',
        headerName: siteTerm.singular,
        width: 150,
        disableColumnMenu: true,
        valueGetter: value => _.get(value, 'name', ''),
      },
      {
        field: 'service',
        headerName: t('projectServices.reporting.columns.service'),
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        valueGetter: value => _.get(value, 'name', ''),
      },
      {
        field: 'crew',
        headerName: t('projectServices.reporting.columns.crew'),
        width: 140,
        disableColumnMenu: true,
        valueGetter: (value, row) => {
          const crew = _.get(row, 'job.crew', null);
          if (!crew) return '';
          const num = _.get(crew, 'crew_number', '');
          const users = _.get(crew, 'users', []);
          const names = users
            .map(u => (u ? `${_.get(u, 'first_name', '')} ${_.get(u, 'last_name', '')}`.trim() : ''))
            .filter(Boolean)
            .join(', ');
          return num ? `${num}. ${names}` : names;
        },
      },
      {
        field: 'price_charged_to_customer',
        headerName: t('projectServices.reporting.columns.revenue'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value || 0),
      },
      {
        field: 'price_paid_to_crew',
        headerName: t('projectServices.reporting.columns.installerCost'),
        width: 130,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value || 0),
      },
      {
        field: 'extra_paid_to_crew',
        headerName: t('projectServices.reporting.columns.extraPay'),
        width: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueFormatter: value => '$' + formatNumberWithCommas(value || 0),
      },
      {
        field: 'profit',
        headerName: t('projectServices.reporting.columns.profit'),
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        valueGetter: (value, row) =>
          (row.price_charged_to_customer || 0) - (row.price_paid_to_crew || 0) - (row.extra_paid_to_crew || 0),
        valueFormatter: value => '$' + formatNumberWithCommas(value || 0),
      },
      {
        field: 'job_id',
        headerName: t('projectServices.reporting.columns.jobId'),
        width: 80,
        disableColumnMenu: true,
        valueGetter: (value, row) => _.get(row, 'job.id', ''),
      },
      {
        field: 'submitted_by',
        headerName: t('projectServices.reporting.columns.submittedBy'),
        width: 140,
        disableColumnMenu: true,
        valueGetter: (value, row) => {
          const user = _.get(row, 'job.submitted_by', null);
          if (!user) return '';
          return `${_.get(user, 'first_name', '')} ${_.get(user, 'last_name', '')}`.trim();
        },
      },
    ],
    [t, siteTerm, siteGroupTerm],
  );

  const revenue = rows.reduce(getTotalReducer('price_charged_to_customer'), 0);
  const cost = rows.reduce(getTotalReducer('price_paid_to_crew'), 0);
  const extraPay = rows.reduce(getTotalReducer('extra_paid_to_crew'), 0);
  const profit = revenue - cost - extraPay;

  const pinnedRows = useMemo(
    () => ({
      bottom: [
        {
          id: '__totals__',
          service_date: t('projectServices.billing.totals'),
          site: { name: '' },
          service: { name: '' },
          price_charged_to_customer: revenue,
          price_paid_to_crew: cost,
          extra_paid_to_crew: extraPay,
          profit,
        },
      ],
    }),
    [revenue, cost, extraPay, profit, t],
  );

  const excelColumns = fromJS([
    {
      id: 'service_date',
      name: t('projectServices.reporting.columns.date'),
      cellRenderer: value => (isValid(value) ? format(value, 'yyyy-MM-dd') : value),
    },
    {
      id: 'site_group_name',
      name: siteGroupTerm.singular,
      cellRenderer: (value, row) => _.get(row?.toJS?.() || row, ['site', 'site_groups', 0, 'name'], ''),
    },
    {
      id: 'site',
      name: siteTerm.singular,
      cellRenderer: value => _.get(value, 'name', ''),
    },
    {
      id: 'service',
      name: t('projectServices.reporting.columns.service'),
      cellRenderer: value => _.get(value, 'name', ''),
    },
    {
      id: 'crew_name',
      name: t('projectServices.reporting.columns.crew'),
      cellRenderer: (value, row) => {
        const r = row?.toJS?.() || row;
        const crew = _.get(r, ['job', 'crew'], null);
        if (!crew) return '';
        return (
          _.get(crew, 'crew_number', '') +
          '. ' +
          _.get(crew, 'users', [])
            .map(u => `${_.get(u, 'first_name', '')} ${_.get(u, 'last_name', '')}`.trim())
            .join(', ')
        );
      },
    },
    {
      id: 'price_charged_to_customer',
      name: t('projectServices.reporting.columns.revenue'),
      cellRenderer: value => '$' + formatNumberWithCommas(value || 0),
    },
    {
      id: 'price_paid_to_crew',
      name: t('projectServices.reporting.columns.installerCost'),
      cellRenderer: value => '$' + formatNumberWithCommas(value || 0),
    },
    {
      id: 'extra_paid_to_crew',
      name: t('projectServices.reporting.columns.extraPay'),
      cellRenderer: value => '$' + formatNumberWithCommas(value || 0),
    },
    {
      id: 'profit_calc',
      name: t('projectServices.reporting.columns.profit'),
      cellRenderer: (value, row) => {
        const r = row?.toJS?.() || row;
        const p =
          (_.get(r, 'price_charged_to_customer', 0) || 0) -
          (_.get(r, 'price_paid_to_crew', 0) || 0) -
          (_.get(r, 'extra_paid_to_crew', 0) || 0);
        return '$' + formatNumberWithCommas(p);
      },
    },
    {
      id: 'job_id_col',
      name: t('projectServices.reporting.columns.jobId'),
      cellRenderer: (value, row) => _.get(row?.toJS?.() || row, ['job', 'id'], ''),
    },
    {
      id: 'submitted_by_col',
      name: t('projectServices.reporting.columns.submittedBy'),
      cellRenderer: (value, row) => {
        const r = row?.toJS?.() || row;
        const user = _.get(r, ['job', 'submitted_by'], null);
        if (!user) return '';
        return `${_.get(user, 'first_name', '')} ${_.get(user, 'last_name', '')}`.trim();
      },
    },
  ]);

  const excelFooter = [
    t('projectServices.billing.totals'),
    '',
    '',
    '',
    '',
    '$' + formatNumberWithCommas(revenue),
    '$' + formatNumberWithCommas(cost),
    '$' + formatNumberWithCommas(extraPay),
    '$' + formatNumberWithCommas(profit),
    '',
    '',
  ];

  const exportButton = (
    <InstallServicesBillingExcelButton
      title={`billing_${format(startDate, 'yyyy-MM-dd')}_to_${format(endDate, 'yyyy-MM-dd')}_job_detail`}
      firstLine={t('projectServices.reporting.excelTitleJobDetail')}
      startDate={format(startDate, 'yyyy-MM-dd')}
      endDate={format(endDate, 'yyyy-MM-dd')}
      columns={excelColumns}
      data={fromJS(rows)}
      footer={excelFooter}
    />
  );

  const showDatePicker = !externalStart;

  const toolbarContent =
    extraToolbarContent ||
    (showDatePicker ? (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ) : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar apiRef={apiRef} showQuickFilter toolbarContent={toolbarContent} toolbarActions={exportButton} />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={row => row.id}
        pinnedRows={rows.length > 0 ? pinnedRows : undefined}
        initialState={{
          sorting: { sortModel: [{ field: 'service_date', sort: 'desc' }] },
        }}
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        density="compact"
        getRowHeight={() => 'auto'}
        getRowClassName={params => (params.row.id === '__totals__' ? 'totals-row' : '')}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'var(--grid-header-bg)',
            borderTop: '1px solid var(--grid-header-border)',
          },
          '& .totals-row': { fontWeight: 'bold' },
        }}
      />
    </Box>
  );
}

export default JobDetailTable;
